import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { imageUrl } from '../lib/images';
import { getCmsSettings } from '../lib/cms';

const DEFAULT_LOGO_URL = 'https://assets.auxodev.dk/assets/e1e86ea9-8aac-4deb-bab2-e96b6ba9dd16/kromix-logo-hvid.png';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    let isMounted = true;
    getCmsSettings().then((data) => {
      if (isMounted && data) setSettings(data);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const logoUrl = settings?.logo?.[0] || DEFAULT_LOGO_URL;
  const telefon = settings?.telefon || '75 73 18 61';
  const email = settings?.email || 'kromix@kromix.dk';
  const adresse = settings?.adresse || 'Hedegårdvej 8';
  const postnummerBy = settings?.postnummer_by || '7323 Give';
  const cvr = settings?.cvr || '34610002';
  const virksomhedsnavn = settings?.virksomhedsnavn || 'KROMIX A/S';
  const bannerOverskrift = settings?.footer_banner_overskrift || 'Har du spørgsmål til KROMIX eller ønsker faglig rådgivning?';
  const bannerTekst = settings?.footer_banner_tekst || 'Vi står altid klar til at rådgive om den optimale ernæring til din hund eller kat.';
  const omTekst = settings?.footer_om_tekst || 'Vi skaffer resultater. Rigtig ernæring - sund levevis. Siden 1986 har KROMIX leveret kompromisløst kvalitetsfoder til danske hunde og katte.';
  const facebookUrl = settings?.facebook_url || 'https://www.facebook.com/KROMIX';


  return (
    <footer className="bg-[#273228] text-[#DED3C0] border-t-4 border-[#71806A]">
      {/* Top Banner inside Footer: Deep Moss Nordic statement section */}
      <div className="bg-[#39463A] py-9 px-4 border-b border-[#71806A]/30">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div>
            <h3 className="text-xl font-bold text-[#FCFAF5] tracking-tight">
              {bannerOverskrift}
            </h3>
            <p className="text-sm text-[#DED3C0] mt-1.5 max-w-2xl leading-relaxed">
              {bannerTekst}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#71806A] hover:bg-[#5C6A56] text-white text-xs font-bold uppercase tracking-wider transition-all shadow-md active:scale-98 whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <span>Kontakt {virksomhedsnavn}</span>
            </Link>
            <a
              href={`tel:${telefon.replace(/\s+/g, '')}`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#FCFAF5] hover:bg-white text-[#39463A] text-xs font-bold uppercase tracking-wider transition-all shadow-md active:scale-98 whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[#71806A]" aria-hidden="true" />
              <span>Ring {telefon}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Col 1: Brand & Presentation */}
          <div className="space-y-4">
            <Link to="/" className="inline-block py-1">
              <img
                src={imageUrl(logoUrl, 'width=240,quality=90,format=auto')}
                alt={virksomhedsnavn}
                className="h-10 w-auto object-contain"
              />
            </Link>
            <p className="text-xs text-[#DED3C0]/90 leading-relaxed">
              {omTekst}
            </p>
            <div className="pt-2">
              <a
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-[#39463A] hover:bg-[#4A5B4C] text-[#FCFAF5] text-xs font-semibold transition-colors border border-[#71806A]/40"
                aria-label={`Besøg ${virksomhedsnavn} på Facebook (åbner i nyt vindue)`}
              >
                <i className="fa-brands fa-facebook text-[#FCFAF5] text-base" aria-hidden="true" />
                <span>Følg os på Facebook</span>
              </a>
            </div>
          </div>

          {/* Col 2: Products Navigation */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#FCFAF5] mb-4 flex items-center gap-2">
              <i className="fa-solid fa-paw text-[#71806A]" aria-hidden="true" />
              <span>Hundefoder Sortiment</span>
            </h4>
            <ul className="space-y-2 text-xs">
              <li>
                <Link to="/produkter/kromix-fiberkost" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  KROMIX Fiberkost (Aktiv hund)
                </Link>
              </li>
              <li>
                <Link to="/produkter/kromix-naturkost" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  KROMIX Naturkost (Alle racer)
                </Link>
              </li>
              <li>
                <Link to="/produkter/kromix-favorit" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  KROMIX Favorit (Højaktiv)
                </Link>
              </li>
              <li>
                <Link to="/produkter/kromix-lam-ris" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  KROMIX Lam & Ris (Sensitiv)
                </Link>
              </li>
              <li>
                <Link to="/produkter/kromix-minies" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  KROMIX Minies (Små racer)
                </Link>
              </li>
              <li>
                <Link to="/produkter/kromix-junior" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  KROMIX Junior (Hvalp 2-20 mdr)
                </Link>
              </li>
              <li>
                <Link to="/produkter/kromix-minikost" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  KROMIX Minikost (Hvalpefoder)
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: General Navigation */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#FCFAF5] mb-4 flex items-center gap-2">
              <i className="fa-solid fa-compass text-[#71806A]" aria-hidden="true" />
              <span>Navigation</span>
            </h4>
            <ul className="space-y-2 text-xs">
              <li>
                <Link to="/hundefoder" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  Hundefoder oversigt
                </Link>
              </li>
              <li>
                <Link to="/kat" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  Kattefoder (KROMIX Katteknas)
                </Link>
              </li>
              <li>
                <Link to="/diverse" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  Fodertilbehør & Diverse
                </Link>
              </li>
              <li>
                <Link to="/josera" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  Josera i Danmark
                </Link>
              </li>
              <li>
                <Link to="/om-kromix" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  Om KROMIX A/S
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="text-[#DED3C0]/85 hover:text-white transition-colors">
                  Kontakt os
                </Link>
              </li>
              <li>
                <Link to="/kurv" className="text-[#BCC6B2] hover:text-white font-semibold transition-colors flex items-center gap-1.5">
                  <i className="fa-solid fa-basket-shopping text-xs" aria-hidden="true" />
                  <span>Webshop Bestillingskurv</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4: Contact info & CVR */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-widest text-[#FCFAF5] mb-4 flex items-center gap-2">
              <i className="fa-solid fa-building text-[#71806A]" aria-hidden="true" />
              <span>Virksomhedsoplysninger</span>
            </h4>
            <div className="space-y-2.5 text-xs text-[#DED3C0]/90">
              <p className="font-bold text-[#FCFAF5] text-sm">{virksomhedsnavn}</p>
              <p className="flex items-start gap-2">
                <i className="fa-solid fa-location-dot text-[#71806A] mt-0.5" aria-hidden="true" />
                <span>
                  {adresse}<br />
                  {postnummerBy}
                </span>
              </p>
              <p className="flex items-center gap-2 pt-1">
                <i className="fa-solid fa-phone text-[#71806A]" aria-hidden="true" />
                <a
                  href={`tel:${telefon.replace(/\s+/g, '')}`}
                  className="hover:text-white transition-colors font-semibold text-[#FCFAF5] whitespace-nowrap"
                >
                  {telefon}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <i className="fa-solid fa-envelope text-[#71806A]" aria-hidden="true" />
                <a
                  href={`mailto:${email}`}
                  className="hover:text-white transition-colors text-[#FCFAF5] whitespace-nowrap"
                >
                  {email}
                </a>
              </p>
              <p className="pt-2 text-[11px] text-[#BCC6B2]">
                CVR: <span className="font-medium text-[#DED3C0] whitespace-nowrap">{cvr}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Bottom copyright & distribution note */}
        <div className="mt-12 pt-6 border-t border-[#71806A]/20 flex flex-col md:flex-row items-center justify-between gap-4 text-[11px] text-[#BCC6B2]">
          <p>
            © {currentYear} KROMIX A/S. Alle rettigheder forbeholdes.
          </p>
          <p className="text-center md:text-right text-[#DED3C0]/70">
            Dansk foderkvalitet siden 1986. Vi skaffer resultater – rigtig ernæring, sund levevis.
          </p>
        </div>
      </div>
    </footer>
  );
}
